import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input,
  Space, Tag, Tooltip, Popconfirm, message,
  Row, Col, Statistic, Typography, Badge, Empty
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, TagsOutlined, TeamOutlined,
  CheckCircleOutlined, WarningOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient, { handleApiError } from '../utils/axiosConfig';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ContactTypeManagement = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [contactTypes, setContactTypes] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingUnused, setDeletingUnused] = useState(false);

  // ─── Fetch ───────────────────────────────────────────────
  const fetchContactTypes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/SupplierContactType');
      setContactTypes(response.data.contactTypes || []);
    } catch (error) {
      message.error(handleApiError(error, t('error_fetching_contact_types')));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const response = await apiClient.get('/api/SupplierContactType/statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchContactTypes();
    fetchStatistics();
  }, []);

  // ─── Modal ───────────────────────────────────────────────
  const openAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  // ─── Save ────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingRecord) {
        await apiClient.put(
          `/api/SupplierContactType/${editingRecord.contactTypeId}`,
          values
        );
        message.success(t('contact_type_updated_successfully'));
      } else {
        await apiClient.post('/api/SupplierContactType', values);
        message.success(t('contact_type_created_successfully'));
      }

      closeModal();
      fetchContactTypes();
      fetchStatistics();
    } catch (error) {
      if (error?.errorFields) return; // form validation
      const msg = handleApiError(
        error,
        editingRecord
          ? t('error_updating_contact_type')
          : t('error_creating_contact_type')
      );
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────
  const handleDelete = async (contactTypeId) => {
    try {
      await apiClient.delete(`/api/SupplierContactType/${contactTypeId}`);
      message.success(t('contact_type_deleted_successfully'));
      fetchContactTypes();
      fetchStatistics();
    } catch (error) {
      message.error(handleApiError(error, t('error_deleting_contact_type')));
    }
  };

  // ─── Delete Unused ───────────────────────────────────────
  const handleDeleteUnused = async () => {
    setDeletingUnused(true);
    try {
      const response = await apiClient.delete('/api/SupplierContactType/unused');
      message.success(
        `${response.data.deletedCount} ${t('unused_contact_types_deleted')}`
      );
      fetchContactTypes();
      fetchStatistics();
    } catch (error) {
      message.error(handleApiError(error, t('error_deleting_unused_contact_types')));
    } finally {
      setDeletingUnused(false);
    }
  };

  // ─── Columns ─────────────────────────────────────────────
  const columns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_, __, index) => (
        <Text type="secondary" style={{ fontSize: '13px' }}>
          {index + 1}
        </Text>
      ),
    },
    {
      title: t('contact_type_name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Space>
          <TagsOutlined style={{ color: '#1890ff' }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      render: (text) =>
        text ? (
          <Text type="secondary">{text}</Text>
        ) : (
          <Text type="secondary" italic>
            {t('no_description')}
          </Text>
        ),
    },
    {
      title: t('contact_count'),
      dataIndex: 'contactCount',
      key: 'contactCount',
      width: 140,
      sorter: (a, b) => a.contactCount - b.contactCount,
      render: (count) => (
        <Badge
          count={count}
          showZero
          style={{
            backgroundColor: count > 0 ? '#1890ff' : '#d9d9d9',
            fontWeight: '600',
          }}
        />
      ),
    },
    {
      title: t('status'),
      key: 'usageStatus',
      width: 130,
      render: (_, record) =>
        record.contactCount > 0 ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t('in_use')}
          </Tag>
        ) : (
          <Tag color="default" icon={<WarningOutlined />}>
            {t('unused')}
          </Tag>
        ),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title={t('edit')}>
            <Button
              type="default"
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('delete_contact_type_confirm')}
            description={
              record.contactCount > 0
                ? t('delete_contact_type_in_use_warning')
                : `"${record.name}" ${t('will_be_deleted')}`
            }
            onConfirm={() => handleDelete(record.contactTypeId)}
            okText={t('yes')}
            cancelText={t('no')}
            okButtonProps={{ danger: true }}
            icon={<WarningOutlined style={{ color: '#ff4d4f' }} />}
          >
            <Tooltip title={t('delete')}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const unusedCount = statistics?.unusedTypes || 0;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{
        background: '#fff',
        padding: '32px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderLeft: '4px solid #722ed1',
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#262626' }}>
              <TagsOutlined style={{ marginRight: '12px', color: '#722ed1' }} />
              {t('contact_type_management')}
            </Title>
            <Text type="secondary" style={{ fontSize: '15px' }}>
              {t('contact_type_management_description')}
            </Text>
          </Col>
          <Col>
            <Space size="middle">
              {unusedCount > 0 && (
                <Popconfirm
                  title={t('delete_unused_confirm_title')}
                  description={`${unusedCount} ${t('delete_unused_confirm_desc')}`}
                  onConfirm={handleDeleteUnused}
                  okText={t('yes')}
                  cancelText={t('no')}
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    size="large"
                    loading={deletingUnused}
                  >
                    {t('delete_unused')} ({unusedCount})
                  </Button>
                </Popconfirm>
              )}
              <Button
                icon={<ReloadOutlined />}
                size="large"
                onClick={() => { fetchContactTypes(); fetchStatistics(); }}
                loading={loading}
              >
                {t('refresh')}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                onClick={openAddModal}
                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1', fontWeight: '600' }}
              >
                {t('add_contact_type')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* ── Statistics ── */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: '12px', background: '#722ed1', border: 'none' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '14px' }}>{t('total_contact_types')}</span>}
              value={statistics?.totalTypes || 0}
              prefix={<TagsOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: '700' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: '12px', background: '#1890ff', border: 'none' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '14px' }}>{t('in_use_types')}</span>}
              value={(statistics?.totalTypes || 0) - (statistics?.unusedTypes || 0)}
              prefix={<CheckCircleOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: '700' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: '12px', background: '#faad14', border: 'none' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '14px' }}>{t('unused_types')}</span>}
              value={statistics?.unusedTypes || 0}
              prefix={<WarningOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: '700' }}
              loading={statsLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Table ── */}
      <Card
        style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        title={
          <Space>
            <TeamOutlined style={{ color: '#722ed1' }} />
            <span style={{ fontWeight: '600' }}>{t('contact_types_list')}</span>
            <Badge
              count={contactTypes.length}
              showZero
              style={{ backgroundColor: '#722ed1' }}
            />
          </Space>
        }
      >
        <Table
          dataSource={contactTypes}
          columns={columns}
          rowKey="contactTypeId"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `${t('total')} ${total} ${t('records')}`,
          }}
          locale={{
            emptyText: (
              <Empty
                description={t('no_contact_types_found')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* ── Add / Edit Modal ── */}
      <Modal
        title={
          <Space>
            {editingRecord ? (
              <EditOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
            ) : (
              <PlusOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
            )}
            <span style={{ fontSize: '18px', fontWeight: '600' }}>
              {editingRecord ? t('edit_contact_type') : t('add_contact_type')}
            </span>
          </Space>
        }
        open={modalVisible}
        onCancel={closeModal}
        footer={[
          <Button key="cancel" onClick={closeModal}>
            {t('cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={handleSave}
            style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
          >
            {editingRecord ? t('update') : t('create')}
          </Button>,
        ]}
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: '16px' }}>
          <Form.Item
            name="name"
            label={t('contact_type_name')}
            rules={[
              { required: true, message: t('please_enter_contact_type_name') },
              { min: 2, message: t('min_2_characters') },
              { max: 100, message: t('max_100_characters') },
            ]}
          >
            <Input
              prefix={<TagsOutlined style={{ color: '#722ed1' }} />}
              placeholder={t('enter_contact_type_name')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('description')}
            rules={[{ max: 500, message: t('max_500_characters') }]}
          >
            <TextArea
              placeholder={t('enter_description_optional')}
              rows={4}
              showCount
              maxLength={500}
            />
          </Form.Item>

          {editingRecord && editingRecord.contactCount > 0 && (
            <div style={{
              padding: '12px 16px',
              background: '#e6f7ff',
              borderRadius: '8px',
              border: '1px solid #91d5ff',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
            }}>
              <InfoCircleOutlined style={{ color: '#1890ff', marginTop: '2px', flexShrink: 0 }} />
              <Text style={{ fontSize: '13px', color: '#1890ff' }}>
                {t('contact_type_in_use_info', { count: editingRecord.contactCount })}
              </Text>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ContactTypeManagement;
